import { t, Trans } from '@lingui/macro';
import { Search, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { Workspace } from "../../models/Workspace";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { useToast } from "../../components/ui/use-toast";
import { cn } from "../../lib/utils";

export const WorkspaceJoinModal: React.FC<{ show: boolean, setShow: (v: boolean) => void }> = ({ show, setShow }) => {
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const { workspaces } = useAppUIContext();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace>();
  const [resultText, setResultText] = useState("");
  const { toast } = useToast();
  const [allowedWorkspaces, setAllowedWorkspaces] = useState<Workspace[]>([]);

  useEffect(() => {
    if (show) {
      setWorkspaceId("");
      setWorkspace(undefined);
      setInProgress(false);
      setResultText("");
      setAllowedWorkspaces([]);
      Fire.ws.getAllowedWorkspaces(Util.getDomain(My.user.email)).then(list => setAllowedWorkspaces(list));
    }
  }, [show]);

  async function findWorkspace() {
    setInProgress(true);
    const ws = await Fire.ws.getWorkspace(workspaceId);
    setInProgress(false);
    setWorkspace(ws);
    setResultText(ws ? "" : "Workspace not found");
  }

  async function joinWorkspace(workspace: Workspace) {
    setInProgress(true);
    try {
      const res = await Fire.ws.joinWorkspace({ workspaceId: workspace.id, invitationKey: null });
      if (res === 'JOINED') {
        toast({
          title: t`Success`,
          description: t`You have joined the workspace ${workspace.name}`
        });
        setShow(false);
        history.replace(`/ws/${workspace.id}`);
      }
      else if (res === 'NOT_ALLOWED') {
        toast({
          variant: "destructive",
          title: t`Access Denied`,
          description: t`You are not allowed to join ${workspace.name}`
        });
      }
      else if (res === 'ALREADY_MEMBER') {
        toast({
          title: t`Info`,
          description: t`You are already a member of ${workspace.name}`
        });
      } else {
        toast({
          variant: "destructive",
          title: t`Error`,
          description: "Unknown error, could not join workspace."
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t`Error`,
        description: `Error: ${(error as Error).message}`
      });
    }
    setInProgress(false);
  }

  function hasJoined(workspace: Workspace) {
    return !!workspaces.find(w => w.id === workspace.id);
  }

  function WorkspaceCard({ workspace }: { workspace: Workspace }) {
    return (
      <Card className="w-full mt-2">
        <CardHeader>
          <CardTitle className="text-lg">{workspace.name}</CardTitle>
          <CardDescription className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">{UtilApp.dateStrToDay(workspace.created, My.user.lang)}</span>
            <div className="flex flex-wrap gap-1">
              {workspace.emailDomains.map(domain => (
                <span key={domain} className="text-xs bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-md">@{domain}</span>
              ))}
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{workspace.description}</p>
        </CardContent>
        <CardFooter className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarImage src={workspace.owner.photoUrl ?? Util.ANONYMOUS_PHOTO_URL} />
              <AvatarFallback>{workspace.owner.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium">{workspace.owner.name}</span>
          </div>
          <Button
            onClick={() => joinWorkspace(workspace)}
            disabled={hasJoined(workspace)}
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Trans>Join</Trans>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  function WorkspaceUICard({ workspace }: { workspace: Workspace }) {
    return (
      <Card className="flex flex-col h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold">{workspace.name}</CardTitle>
          <div className="flex flex-col gap-1 mt-1">
            <span className="text-xs text-muted-foreground">{UtilApp.dateStrToDay(workspace.created, My.user.lang)}</span>
            <div className="flex flex-wrap gap-1">
              {workspace.emailDomains.map(domain => (
                <span key={domain} className={cn("text-xs px-1.5 py-0.5 rounded-md", Util.getDomain(My.user.email) === domain ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground")}>
                  @{domain}
                </span>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 pb-3">
          <p className="text-xs text-muted-foreground line-clamp-3">{workspace.description}</p>
        </CardContent>
        <CardFooter className="flex justify-between items-center pt-0">
          <div className="flex items-center gap-2">
            <Avatar className="h-5 w-5">
              <AvatarImage src={workspace.owner.photoUrl ?? Util.ANONYMOUS_PHOTO_URL} />
              <AvatarFallback className="text-[10px]">{workspace.owner.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium truncate max-w-[80px]">{workspace.owner.name}</span>
          </div>
          <Button
            onClick={() => joinWorkspace(workspace)}
            disabled={hasJoined(workspace)}
            size="sm"
            className="h-7 text-xs bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Trans>Join</Trans>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogContent className="sm:max-w-md md:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><Trans>Join a workspace</Trans></DialogTitle>
        </DialogHeader>

        {inProgress && (
          <div className="flex justify-center p-2 w-full">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium"><Trans>Workspace ID</Trans></label>
            <div className="flex w-full items-center space-x-2">
              <Input
                className="flex-1"
                value={workspaceId}
                onKeyDown={e => UtilApp.runOnEnter(e, findWorkspace)}
                onChange={e => setWorkspaceId(e.target.value ?? "")}
                placeholder={t`Enter a Workspace ID`}
                maxLength={24}
                disabled={inProgress}
              />
              <Button type="button" size="icon" disabled={!workspaceId} onClick={findWorkspace} variant="secondary">
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {resultText && <p className="text-sm text-muted-foreground">{resultText}</p>}
          </div>

          {workspace && <WorkspaceCard workspace={workspace} />}

          {allowedWorkspaces.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-semibold mb-4 text-foreground">
                <Trans>Workspaces found for your email domain "{Util.getDomain(My.user.email)}".</Trans>
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allowedWorkspaces.map(ws => <WorkspaceUICard key={ws.id} workspace={ws} />)}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WorkspaceJoinModal;